// @flow
/** @jsx jsx */

import React, { type Node } from 'react';
import { capitalize } from 'lodash';

import { PaymentMethodImage } from '@zapier/payment';
import { Text } from '@zapier/common-components';
import { getCardDefinitionFromBrand } from '@zapier/payment/src/utils';
import {
  type PaymentDetailsBank,
  type PaymentDetailsCard,
  type PaymentSource,
  paymentSourceMethods,
} from '@zapier/payment/src/types';
import { css, jsx } from '@emotion/core';

const detailsCss = (isCompact = false, isInline = false) =>
  css(
    {
      display: 'flex',
      flexDirection: 'column',
      lineHeight: '18px',
      marginLeft: isCompact ? 10 : 15,
      padding: 0,
    },
    isInline && {
      alignItems: 'center',
      flexDirection: 'row',
    }
  );

const numbersCss = (isInline = false) => ({
  marginLeft: isInline ? 5 : 0,
});

const wrapperCss = {
  alignItems: 'center',
  display: 'flex',
};

type ChildrenProps = {
  descriptions: Object,
  isInline?: boolean,
};

type Props = {
  children?: ChildrenProps => Node,
  isCompact?: boolean,
  isInline?: boolean,
  /** Current payment source */
  paymentSource: PaymentSource,
};

type Descriptions = {
  imageType: string,
  default: {
    firstLine: string,
    secondLine: string,
  },
  compact: {
    firstLine: string,
    secondLine: string,
  },
};

const getPaymentDescriptions = ({ details, type }): Descriptions => {
  switch (type) {
    case paymentSourceMethods.card: {
      const cardDetails: PaymentDetailsCard = (details: any);
      const definition = getCardDefinitionFromBrand(cardDetails.brand);
      return {
        imageType: definition.type,
        default: {
          firstLine: `${definition.niceType} ending in ${cardDetails.last4}`,
          secondLine: `Expires ${cardDetails.expiry}`,
        },
        compact: {
          firstLine: `${definition.niceType}`,
          secondLine: `${cardDetails.last4}`,
        },
      };
    }
    case paymentSourceMethods.bank: {
      const bankDetails: PaymentDetailsBank = (details: any);
      return {
        imageType: type,
        default: {
          firstLine: `${bankDetails.bankName} ${capitalize(
            bankDetails.accountType
          )}`,
          secondLine: `Ending in ${bankDetails.last4}`,
        },
        compact: {
          firstLine: `${capitalize(bankDetails.accountType)}`,
          secondLine: `${bankDetails.last4}`,
        },
      };
    }
    default:
      return {
        imageType: 'unknown',
        default: {
          firstLine: '',
          secondLine: '',
        },
        compact: {
          firstLine: '',
          secondLine: '',
        },
      };
  }
};

const renderDefaultInfo = ({ descriptions }: ChildrenProps) => {
  return (
    <React.Fragment>
      <Text type="body-strong">{descriptions.default.firstLine}</Text>
      <Text color="steel" type="body">
        {descriptions.default.secondLine}
      </Text>
    </React.Fragment>
  );
};

const renderCompactInfo = ({ descriptions, isInline }: ChildrenProps) => (
  <React.Fragment>
    <Text type="body-strong">{descriptions.compact.firstLine}</Text>
    <span css={numbersCss(isInline)}>
      <Text color="steel" type="body">
        {descriptions.compact.secondLine}
      </Text>
    </span>
  </React.Fragment>
);

const PaymentInfo = ({
  isCompact = false,
  isInline = false,
  children = isCompact ? renderCompactInfo : renderDefaultInfo,
  paymentSource,
}: Props) => {
  const descriptions = getPaymentDescriptions(paymentSource);
  return (
    <div css={wrapperCss}>
      <PaymentMethodImage
        imageType={descriptions.imageType}
        isSmall={isCompact}
      />
      <div css={detailsCss(isCompact, isInline)}>
        {children({ descriptions, isInline })}
      </div>
    </div>
  );
};

export default PaymentInfo;
